import type { PowerSettings, PowerState } from "@foreman/protocol";
import type { KeyValue } from "./computers";
import type { ConnState } from "./connection";

/** Where, on the computer's own network, a wake packet for it goes. Learned while the computer is online; only ever held on this phone. */
export interface WakeTarget {
  mac: string;
  broadcast: string;
  /** The computer said whether it is set up to wake on a network packet (null: it could not tell). */
  enabled: boolean | null;
}

interface Record_ {
  /** What the person asked for and the computer has not confirmed yet (the computer was offline, or has not answered). */
  desired: PowerSettings | null;
  wake: WakeTarget | null;
}

const KEY = "foreman.power.v1";

export const samePowerSettings = (a: PowerSettings, b: PowerSettings) => a.keepAwake === b.keepAwake && a.onBattery === b.onBattery && a.timeoutMin === b.timeoutMin;

/** Per-computer availability choices that are waiting to reach the computer, and where to wake it. Settings themselves live on the computer. */
export class PowerPrefs {
  private data: Record<string, Record_>;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly kv: KeyValue) {
    this.data = this.load();
  }

  get(id: string): Record_ {
    return this.data[id] ?? { desired: null, wake: null };
  }

  setDesired(id: string, desired: PowerSettings | null): void {
    this.put(id, { ...this.get(id), desired });
  }

  setWake(id: string, wake: WakeTarget | null): void {
    const cur = this.get(id).wake;
    if (cur === wake || (cur && wake && cur.mac === wake.mac && cur.broadcast === wake.broadcast && cur.enabled === wake.enabled)) return;
    this.put(id, { ...this.get(id), wake });
  }

  forget(id: string): void {
    if (!(id in this.data)) return;
    const { [id]: _gone, ...rest } = this.data;
    this.data = rest;
    this.save();
  }

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  private put(id: string, rec: Record_): void {
    this.data = { ...this.data, [id]: rec };
    this.save();
    for (const l of [...this.listeners]) l();
  }

  private save(): void {
    try {
      this.kv.set(KEY, JSON.stringify(this.data));
    } catch {
      /* storage can be unavailable; it still works for this session */
    }
  }

  private load(): Record<string, Record_> {
    try {
      const raw = this.kv.get(KEY);
      const parsed = raw ? (JSON.parse(raw) as unknown) : null;
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, Record_>) : {};
    } catch {
      return {};
    }
  }
}

/** Why waking a computer is or is not possible right now. */
export type WakeSupport =
  /** A packet can be sent: this phone knows where the computer is, and another of its computers is online to send it. */
  | "available"
  /** This phone has not yet seen the computer online with a wake-capable Brocoded, so it does not know where to send the packet. */
  | "unknown"
  /** The computer said it is not set up to wake on a network packet. */
  | "needs-setup"
  /** Waking needs another computer of yours, on the same network, to be online. */
  | "needs-helper";

export interface WakeStatus {
  phase: "sending" | "waking" | "failed";
  /** Tries so far in the current window. */
  attempts: number;
  since: number;
  /** Why a failed attempt failed. */
  reason?: "not_sent" | "timeout" | "limit";
  message?: string;
}

/** What a person is told about a computer: one word, one line, and what they can do. */
export interface Availability {
  kind: "ready" | "connecting" | "waking" | "wake-failed" | "offline" | "unauthorized";
  label: string;
  message: string;
  /** A Wake action makes sense (offline or failed, and a packet can be sent). */
  canWake: boolean;
}

export const WAKE_ATTEMPT_LIMIT = 3;

/**
 * The truth about a computer, from what we actually observe. "Online" is only ever the live connection to the computer: sending a
 * wake packet moves a computer to "Waking", never to ready. A computer that vanished could be asleep, off or without network, and
 * nothing can tell those apart from here, so offline says so rather than claiming "sleeping".
 */
export function describeAvailability(state: ConnState, wake: WakeStatus | undefined, support: WakeSupport): Availability {
  if (state === "ready") return { kind: "ready", label: "Online", message: "Your computer is online and ready.", canWake: false };
  if (state === "unauthorized") return { kind: "unauthorized", label: "Not authorized", message: "This phone was removed from the computer. Pair it again.", canWake: false };
  if (state === "connecting" || state === "reconnecting") return { kind: "connecting", label: "Connecting", message: "Reaching your computer.", canWake: false };
  if (wake?.phase === "sending" || wake?.phase === "waking") return { kind: "waking", label: "Waking", message: "Waiting for your computer to come back online. This can take a minute.", canWake: false };
  const canWake = support === "available";
  if (wake?.phase === "failed") {
    const why = wake.reason === "limit" ? "Too many attempts for now. Try again in a few minutes." : wake.reason === "not_sent" ? wake.message ?? "The wake signal could not be sent." : "It did not come back online. It may be turned off, or not set up to wake.";
    return { kind: "wake-failed", label: "Wake failed", message: why, canWake: canWake && wake.reason !== "limit" };
  }
  const message =
    support === "available"
      ? "Your computer is offline. It may be asleep: you can try to wake it."
      : support === "needs-setup"
        ? "Wake support requires configuration on this computer. Open Remote Availability for the steps."
        : support === "needs-helper"
          ? "This computer is offline. To wake it, another of your computers on the same network must be online."
          : "This computer is offline and cannot currently be woken remotely.";
  return { kind: "offline", label: "Offline", message, canWake };
}

export type { PowerSettings, PowerState };
